import { redirect } from '@tanstack/react-router';
import type { ParsedLocation } from '@tanstack/react-router';

import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { flagKeyForCode } from '~/constants/config/feature-gates';
import { switchSubsidiary } from '~/features/auth/services/auth.service';
import { refreshAuthUser } from '~/features/auth/services/refresh-auth-user';
import { getMyFeatureFlags } from '~/features/platform/services/feature-flag.service';
import { getSubsidiaryProducts } from '~/features/platform/services/product-catalog.service';
import type { SubsidiaryProduct } from '~/features/platform/services/product-catalog.service';
import { useAuthStore } from '~/store/auth.store';
import { useTenantStore } from '~/store/tenant.store';
import { PLATFORM_PRODUCT_CODES, useTrialStore } from '~/store/trial.store';
import type { PlatformContractType, TrialPlatform } from '~/store/trial.store';
import { hasScreenAccess, isSystemSubsidiaryAdmin, isTenantAdmin } from '~/utils/access';
import { getTokenSubsidiaryId } from '~/utils/access-token';

// Re-exported from the shared access module (`~/utils/access`) — the single
// source of truth these guards and the sidebar visibility logic both consume.
// Kept exported here for existing importers (post-login-redirect, useTenantAdmin).
export { TENANT_ADMIN_ROLES } from '~/utils/access';

/**
 * Route guard: requires the user to be authenticated.
 * Redirects to /login if not authenticated, forwarding the original URL
 * (path + search + hash) via a `redirect` query param so the user lands
 * on their requested page after auth. Use as `beforeLoad` on layout routes.
 */
export function requireAuth({ location }: { location: ParsedLocation }) {
  const { isAuthenticated, isInitializing } = useAuthStore.getState();
  if (!isInitializing && !isAuthenticated) {
    // TanStack Router uses throw redirect() as control flow — not an error
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/login',
      search: { redirect: location.href },
    });
  }
}

/**
 * Route guard: requires a tenant-admin role (one of `TENANT_ADMIN_ROLES`).
 * Super admins bypass all checks.
 * Returns a `beforeLoad` function that redirects to fallback if the user's roleCode
 * is not in the allowed list.
 */
export function requireTenantAdmin(fallback = '/') {
  return () => {
    const { user } = useAuthStore.getState();
    // Same predicate the sidebar uses via `canAccessNavPath` — see `~/utils/access`.
    if (isTenantAdmin(user)) return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: fallback });
  };
}

/**
 * Route guard: requires a specific global permission.
 * Super admins bypass permission checks.
 * Returns a `beforeLoad` function that redirects to fallback if the user lacks the permission.
 */
export function requirePermission(permission: string, fallback = '/') {
  return () => {
    const { user } = useAuthStore.getState();
    // Same predicate the sidebar uses via `canAccessNavPath` — see `~/utils/access`.
    // Handles super-admin / tenant-admin bypass, direct match, and resource:read.
    if (hasScreenAccess(user, permission)) return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: fallback });
  };
}

/**
 * Route guard: requires the user to be a system admin (isSuperAdmin).
 * Returns a `beforeLoad` function that redirects to fallback if the user
 * is not a super admin.
 */
export function requireSystemAdmin(fallback = '/') {
  return () => {
    const { user } = useAuthStore.getState();
    if (!user?.isSuperAdmin) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: fallback });
    }
  };
}

/**
 * Route guard: requires a Lily AI platform admin — `isSuperAdmin` AND the active
 * subsidiary is the system subsidiary (identity-service's SYSTEM_ADMIN rule). Used by
 * Taxonomy Studio, whose API refuses anyone else. Same predicate as the sidebar
 * (`canAccessNavPath('/taxonomy')`), so the menu never offers a page that redirects.
 */
export function requireSystemSubsidiaryAdmin(fallback = '/') {
  return () => {
    const { user } = useAuthStore.getState();
    if (isSystemSubsidiaryAdmin(user)) return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: fallback });
  };
}

/** Maximum time the feature-flag guards wait for `/feature-flags/me` before
 *  falling back to the store-only check. Keeps deep-link navigation responsive
 *  when the API is slow. */
const REQUIRE_FEATURE_TIMEOUT_MS = 1500;

/**
 * Shared resolver used by both feature-flag route guards. Awaits
 * `/feature-flags/me` (with a hard timeout), then evaluates the flag with strict
 * semantics: the API result wins when present; the auth/tenant stores are only
 * consulted when the API call fails or times out (deep-link cold-load before
 * `useFeatureFlagsSync` has hydrated). "First map with the key wins" matches
 * the sidebar's `nav-main.tsx` policy so visibility and route gating can never
 * disagree.
 */
export async function resolveFlag(flagKey: string): Promise<boolean> {
  let resolved: Record<string, boolean> | null = null;
  try {
    const data = await Promise.race([
      queryClient.ensureQueryData({
        queryKey: queryKeys.featureFlags.me(),
        queryFn: getMyFeatureFlags,
      }),
      new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('feature-flag resolve timeout'));
        }, REQUIRE_FEATURE_TIMEOUT_MS);
      }),
    ]);
    resolved = data.flags;
  } catch {
    resolved = null;
  }

  if (resolved !== null) {
    return resolved[flagKey] === true;
  }
  const authFlags = useAuthStore.getState().user?.featureFlags;
  const tenantFlags = useTenantStore.getState().config?.featureFlags;
  if (authFlags && flagKey in authFlags) return authFlags[flagKey] === true;
  if (tenantFlags && flagKey in tenantFlags) return tenantFlags[flagKey] === true;
  return false;
}

/**
 * Route guard: requires a feature flag to be enabled. Same resolution policy as
 * `requireFeatureGate` but takes the flag key directly (skipping the registry
 * lookup) — useful for legacy callers and ad-hoc gates.
 */
export function requireFeature(flagKey: string, fallback = '/') {
  return async () => {
    const enabled = await resolveFlag(flagKey);
    if (!enabled) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: fallback });
    }
  };
}

/**
 * Route guard for feature-flag-gated pages. Looks `code` up in the FEATURE_GATES
 * registry; if the code is NOT registered the route renders normally (permission
 * checks remain the user's only barrier). If the code IS registered, awaits
 * /feature-flags/me and redirects to `/access-denied` (or any provided fallback)
 * when the flag isn't enabled.
 *
 * Use this on every flag-gated page's beforeLoad. Pair with `useFeatureGate(code)`
 * in components for the matching sidebar visibility check.
 */
export function requireFeatureGate(code: string, fallback = '/access-denied') {
  return async () => {
    const flagKey = flagKeyForCode(code);
    if (flagKey === undefined) return;
    const enabled = await resolveFlag(flagKey);
    if (!enabled) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: fallback });
    }
  };
}

/** Maximum time the contract guard waits for the products API before failing open. */
const REQUIRE_CONTRACT_TIMEOUT_MS = 1500;

/**
 * Route guard: requires a specific contract type for a platform product.
 *
 * Fast path: checks trial.store (populated for in-app navigation via useTrialSync).
 * Cold-load path: fetches enrolled products via queryClient.ensureQueryData when
 * the store hasn't been hydrated yet (deep-link or hard refresh).
 *
 * Redirects to /product-not-enrolled?product=<platform> when the platform's
 * contractType is missing, null, or not in requiredTypes. Fails open on API
 * timeout/error to avoid blocking legitimate users.
 */
export function requireContract(platform: TrialPlatform, requiredTypes: PlatformContractType[]) {
  return async () => {
    const subsidiaryId = useAuthStore.getState().user?.activeTenantId;
    // Auth guard handles the unauthenticated case — bail out cleanly here.
    if (!subsidiaryId) return;

    // Fast path: store already populated by useTrialSync (typical in-app navigation).
    const { enrolledContracts } = useTrialStore.getState();
    if (Object.keys(enrolledContracts).length > 0) {
      const contractType = enrolledContracts[platform];
      if (!contractType || !requiredTypes.includes(contractType)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/product-not-enrolled', search: { product: platform } });
      }
      return;
    }

    // Cold-load path: store not yet hydrated — fetch products directly.
    let products: SubsidiaryProduct[] | null = null;
    try {
      products = await Promise.race([
        queryClient.ensureQueryData({
          queryKey: queryKeys.products.bySubsidiary(subsidiaryId),
          queryFn: () => getSubsidiaryProducts(subsidiaryId),
        }),
        new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('contract-guard timeout'));
          }, REQUIRE_CONTRACT_TIMEOUT_MS);
        }),
      ]);
    } catch {
      // Fail open on timeout or API error — same policy as resolveFlag in requireFeatureGate.
      products = null;
    }

    if (products === null) return;

    const productCode = PLATFORM_PRODUCT_CODES[platform];
    const product = products.find((p) => p.productCode === productCode);
    const contractType = (product?.contractType ?? null) as PlatformContractType | null;

    if (!contractType || !requiredTypes.includes(contractType)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/product-not-enrolled', search: { product: platform } });
    }
  };
}

// ---------------------------------------------------------------------------
// Subsidiary URL reconciliation (?sub=<id>) — see docs/subsidiary-url-identifier.md
// ---------------------------------------------------------------------------

/** One switch attempt per target id per tab, so a persistent mismatch can't loop. */
const reconcileKey = (subId: string) => `sub-reconcile:${subId}`;
/** One-shot flag read by the tenant layout to fire the "no access" toast. */
const SUB_DENIED_KEY = 'sub-denied';

// sessionStorage can throw (private mode / disabled) — never let bookkeeping
// take the page down; losing a marker only weakens the loop guard, still safe.
function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}
function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Reads and clears the one-shot "subsidiary access denied" marker. Returns the
 * attempted subsidiary id if a denial is pending (so the tenant layout can toast
 * exactly once), else null. Consuming clears it so a refresh/share won't re-fire.
 */
export function consumeSubsidiaryDenied(): string | null {
  const denied = safeSessionGet(SUB_DENIED_KEY);
  if (denied) safeSessionRemove(SUB_DENIED_KEY);
  return denied;
}

/**
 * Route guard (on `_tenant`): reconciles the `?sub=<id>` URL param against the
 * active subsidiary so authenticated URLs are shareable. Sequence:
 * repair token drift → back-fill when absent → render when it matches → on a
 * mismatch, switch to `sub` and reload if accessible, else land on the current
 * subsidiary and flag a "no access" toast. Runs after `requireAuth` +
 * `/auth/me` hydration.
 */
export async function reconcileSubsidiary({
  search,
  location,
}: {
  search: { sub?: string | undefined };
  location: ParsedLocation;
}) {
  const { user, isImpersonating, accessToken } = useAuthStore.getState();
  let active = user?.activeTenantId;
  // No user / no resolvable active subsidiary — auth + bootstrap own these cases.
  if (!active) return;

  // (0) Repair store↔token drift before trusting `active`. Every tenant-owned
  // read is scoped by the access token's `subsidiaryId` claim — never by the
  // `X-Subsidiary-ID` header (workflow-service applies it as a Hibernate
  // @TenantId from the claim; identity-service resolves JWT-first). So when the
  // store disagrees with the token, the UI, the URL and the data each describe a
  // different subsidiary and every tenant-scoped deep link 404s with no visible
  // cause. `/auth/me` reports the token's subsidiary, so re-hydrating realigns
  // the store here — on any navigation, with no manual refresh.
  const tokenSubsidiaryId = getTokenSubsidiaryId(accessToken);
  if (tokenSubsidiaryId && tokenSubsidiaryId !== active) {
    await refreshAuthUser();
    active = useAuthStore.getState().user?.activeTenantId;
    if (!active) return;
  }

  const sub = search.sub?.trim();

  // (1) Absent/empty → back-fill so the address bar always shows a copyable id.
  if (!sub) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect is control flow
    throw redirect({ to: '.', replace: true, search: (prev) => ({ ...prev, sub: active }) });
  }

  // (2) Match → render the requested URL as-is; clear any stale loop marker.
  if (sub === active) {
    safeSessionRemove(reconcileKey(sub));
    return;
  }

  // (3) Mismatch. Impersonation owns the token — cannot switch; silently fall
  // back to the active subsidiary (not a permission problem, so no toast).
  // Post-(0) `active` is the impersonation token's own subsidiary, so this
  // back-fill now describes the session actually in force rather than the
  // admin's pre-impersonation subsidiary.
  if (isImpersonating) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '.', replace: true, search: (prev) => ({ ...prev, sub: active }) });
  }

  // Re-read from the store rather than the `user` captured above — step (0) may
  // have replaced it, and a stale `accessibleTenants` would deny a switch the
  // refreshed profile actually permits.
  const accessibleTenants = useAuthStore.getState().user?.accessibleTenants ?? [];
  const hasAccess = accessibleTenants.some((t) => t.id === sub);
  const alreadyTried = safeSessionGet(reconcileKey(sub)) === '1';

  // (3a) Accessible and not already attempted → switch, then reload onto the
  // exact requested URL. The reload re-runs bootstrap so `active` becomes `sub`
  // and step (2) renders. If the switch call fails, fall through to denial.
  if (hasAccess && !alreadyTried) {
    safeSessionSet(reconcileKey(sub), '1');
    const switched = await switchSubsidiary(sub)
      .then(() => true)
      .catch(() => false);
    if (switched) {
      // Full reload onto the same URL — bootstrap re-fetches /auth/me so `active`
      // becomes `sub` and step (2) renders. Return: the document is unloading.
      window.location.replace(location.href);
      return;
    }
  }

  // (3b) No access (or switch failed / loop guard tripped) → land on the active
  // subsidiary and flag the one-shot toast for the tenant layout to fire.
  safeSessionRemove(reconcileKey(sub));
  safeSessionSet(SUB_DENIED_KEY, sub);
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '.', replace: true, search: (prev) => ({ ...prev, sub: active }) });
}
